import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useBrand } from '../contexts/BrandContext';
import { formatCurrency } from '../lib/utils';
import {
    CheckCircle, Headphones, Ticket, MapPin, Clock, Users,
    Download, Copy, ExternalLink, ArrowLeft, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrderSuccessPage() {
    const [params] = useSearchParams();
    const brand = useBrand();
    const orderId = params.get('order_id');
    const sessionId = params.get('session_id');

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retries, setRetries] = useState(0);

    useEffect(() => {
        if (orderId) pollOrderStatus();
    }, [orderId]);

    const pollOrderStatus = async () => {
        try {
            const url = sessionId
                ? `${API}/api/b2c/order/${orderId}/status?session_id=${sessionId}`
                : `${API}/api/b2c/order/${orderId}/status`;
            const res = await fetch(url);
            if (!res.ok) throw new Error();
            const data = await res.json();
            setOrder(data);

            if (data.payment_status !== 'paid' && retries < 10) {
                setTimeout(() => setRetries(r => r + 1), 2000);
            }
        } catch { }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (retries > 0 && retries <= 10 && order?.payment_status !== 'paid') {
            pollOrderStatus();
        }
    }, [retries]);

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied!');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-4" />
                    <p className="text-white font-outfit">Confirming your payment...</p>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center px-4">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h2 className="text-xl font-outfit font-bold text-white mb-2">Order Not Found</h2>
                    <p className="text-muted-foreground mb-6">We couldn't find this order. Please check your email for confirmation.</p>
                    <Link to="/"><Button className="bg-gold text-[#0A0D14]">Back to Home</Button></Link>
                </div>
            </div>
        );
    }

    const isPaid = order.payment_status === 'paid';
    const currency = order.currency?.toUpperCase() || 'EUR';

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <>
                                <ArrowLeft className="w-5 h-5 text-white/60" />
                                <span className="font-outfit font-semibold text-white">{brand.platform_name}</span>
                            </>
                        )}
                    </Link>
                </div>
            </header>

            <div className="pt-20 pb-24 px-4 sm:px-6 max-w-3xl mx-auto">
                {/* Status */}
                <div className="text-center mb-8">
                    {isPaid ? (
                        <>
                            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-5">
                                <CheckCircle className="w-10 h-10 text-green-400" />
                            </div>
                            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="success-title">Booking Confirmed!</h1>
                            <p className="text-muted-foreground">A confirmation email has been sent to <span className="text-white">{order.customer_email}</span></p>
                        </>
                    ) : (
                        <>
                            <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-5" />
                            <h1 className="font-outfit text-2xl font-bold text-white mb-2">Processing Payment...</h1>
                            <p className="text-muted-foreground">Please wait while we confirm your payment</p>
                        </>
                    )}
                </div>

                {isPaid && (
                    <div className="space-y-6">
                        {/* Order Details */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 sm:p-6 space-y-4">
                                <h3 className="font-outfit font-semibold text-white">Order Details</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Experience</span>
                                        <span className="text-white font-medium">{order.listing_title}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Package</span>
                                        <span className="text-white flex items-center gap-1.5">
                                            <Ticket className="w-3.5 h-3.5 text-gold" /> {order.option_name || 'Experience'}
                                        </span>
                                    </div>
                                    {order.visit_date && (
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Visit</span>
                                            <span className="text-white flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {order.visit_date} at {order.visit_time}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Travelers</span>
                                        <span className="text-white">
                                            {order.adults > 0 && `${order.adults} Adult`}{order.children > 0 && `, ${order.children} Child`}{order.infants > 0 && `, ${order.infants} Infant`}
                                        </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-white/10">
                                        <span className="text-white font-medium">Total Paid</span>
                                        <span className="text-lg font-outfit font-bold text-gold">{formatCurrency(order.total_amount, currency)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Audio Guide Codes */}
                        {order.audio_vouchers?.length > 0 && (
                            <Card className="bg-[#121620] border-blue-500/20">
                                <CardContent className="p-5 sm:p-6 space-y-4">
                                    <h3 className="font-outfit font-semibold text-white flex items-center gap-2">
                                        <Headphones className="w-4 h-4 text-blue-400" /> Your Audio Guide Codes
                                    </h3>
                                    <p className="text-xs text-muted-foreground">Use these codes to access your audio guides. Enter them at the access page.</p>
                                    <div className="space-y-2">
                                        {order.audio_vouchers.map((v, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                                                <code className="text-white font-mono text-sm sm:text-base tracking-wider" data-testid={`voucher-code-${i}`}>{v.code}</code>
                                                <Button variant="ghost" size="sm" onClick={() => copyCode(v.code)} className="text-gold hover:text-gold/80 gap-1">
                                                    <Copy className="w-4 h-4" /> Copy
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    <Link to="/access">
                                        <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white gap-2" data-testid="access-guide-btn">
                                            <Headphones className="w-4 h-4" /> Access Audio Guide Now
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )}

                        {/* Tickets */}
                        {order.tickets_assigned?.length > 0 && (
                            <Card className="bg-[#121620] border-gold/20">
                                <CardContent className="p-5 sm:p-6 space-y-4">
                                    <h3 className="font-outfit font-semibold text-white flex items-center gap-2">
                                        <Ticket className="w-4 h-4 text-gold" /> Your Entry Tickets
                                    </h3>
                                    <div className="space-y-2">
                                        {order.tickets_assigned.map((t, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                                                <span className="text-white text-sm capitalize">{t.type} Ticket</span>
                                                <a href={`${API}/api/files/${t.file_path}`} target="_blank" rel="noopener noreferrer">
                                                    <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80 gap-1">
                                                        <Download className="w-4 h-4" /> Download
                                                    </Button>
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Manual handling notice */}
                        {order.delivery_status === 'manual' && (
                            <Card className="bg-[#121620] border-yellow-500/20">
                                <CardContent className="p-5 sm:p-6">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-white font-medium mb-1">Tickets Being Processed</h4>
                                            <p className="text-sm text-muted-foreground">Some tickets are being prepared and will be sent to your email shortly. Your audio guide codes are ready to use immediately.</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link to="/" className="flex-1">
                                <Button variant="outline" className="w-full border-white/10 text-white">Back to Home</Button>
                            </Link>
                            <Link to="/access" className="flex-1">
                                <Button className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2">
                                    <Headphones className="w-4 h-4" /> Start Listening
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
